/* =====================================================================
 * AuthGuard — 인증 상태에 따라 자식 렌더링 또는 로그인 페이지 표시
 *
 * BrowserRouter 내에서 인증이 필요한 라우트를 감쌉니다.
 * - 인증 확인 중 → AppSkeleton
 * - 미인증 + 팀 초대 토큰 → Layout + InviteHomePage
 * - 미인증 → AuthPage
 * - 인증 완료 → children (Outlet)
 * ===================================================================== */

import React, { Suspense, useState, useEffect } from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { resolveTenantDefaultPath, useTenant } from '@auw/tenant';
import { AppSkeleton } from '@auw/workspace-ui';
import { AuthPage } from '@/components/auth/AuthPage';
import { InviteHomePage } from '@/components/auth/InviteHomePage';
import { SEOHead } from './SEOHead';
import { useUserStore } from '@auw/stores';
import { hasAuthToken } from '@auw/api-client';
import { getActiveAuthAccount } from '@auw/api-client';
import { lazyWithRetry } from '@auw/workspace-utils';

const Layout = lazyWithRetry(() =>
  import('@/components/layout/Layout').then((m) => ({ default: m.Layout })),
);

const SKELETON_DELAY_MS = 500;

interface AuthGuardProps {
  isAuthed: boolean;
  authChecking: boolean;
  inviteToken: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  isAuthed,
  authChecking,
  inviteToken,
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryInvite = searchParams.get('teamInvite') || '';
  const token = inviteToken || queryInvite;
  const user = useUserStore((s) => s.user);
  const tenant = useTenant();
  const defaultPath = resolveTenantDefaultPath(tenant);
  /**
   * Suppress the loading skeleton while we're on the tenant's landing
   * path and the user is (or is about to be) authed — showing the
   * skeleton would cause a flash on top of the actual dashboard.
   * Pre-Phase-4c this path was hard-coded to `/widgets`.
   */
  const suppressLoadingSkeleton = location.pathname === defaultPath
    && (Boolean(user) || hasAuthToken() || !!getActiveAuthAccount());

  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!authChecking) {
      setShowSkeleton(false);
      return;
    }
    const id = window.setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [authChecking]);

  if (authChecking) {
    if (suppressLoadingSkeleton) return null;
    return showSkeleton ? <AppSkeleton /> : null;
  }

  if (!isAuthed) {
    if (token) {
      return (
        <Suspense fallback={<AppSkeleton />}>
          <Layout />
          <InviteHomePage inviteToken={token} />
        </Suspense>
      );
    }
    return (
      <>
        <SEOHead
          title="AI 에이전트 워크스페이스"
          description="AI 에이전트와 대화하고, 문서를 작성하고, 이미지를 생성하세요. 팀 협업을 위한 올인원 AI 워크스페이스."
        />
        <AuthPage />
      </>
    );
  }

  return <Outlet />;
};
